import { useState } from 'react'

import { Layout, Button } from '@loadsmart/miranda-react'

import { isQuoteExpired } from '../domain/Quote'
import { type AccessorialCharge } from '../domain/QuoteAccessorial'
import { type SpotQuote, isRateRejected } from '../domain/SpotQuote'
import { ConfirmRejectDialog } from './ConfirmRejectDialog'
import { ConfirmSubmissionDialog } from './ConfirmSubmissionDialog'
import { QuoteRateInputFields } from './QuoteRateInputFields'
import { SubmittedRateValue } from './SubmittedRateValue'

export type BidRateFieldProps = Readonly<{
  isSubmitted: boolean
  currentFlatRate?: number
  disabled?: boolean
  initialFlatRate: number
  initialPerMileRate: number
  mileage?: number
  onRateChange: (flatRate: number) => void
  submittedAt?: Date
  submitDisabled?: boolean
  onSubmit: () => void
  spotQuote: SpotQuote
  accessorialCharges?: Array<AccessorialCharge>
  handleRejectRate?: (reason: string) => void
}>

export function SingleBidRateField({
  isSubmitted,
  mileage,
  initialFlatRate,
  initialPerMileRate,
  disabled,
  onRateChange,
  currentFlatRate,
  submittedAt,
  submitDisabled,
  accessorialCharges,
  onSubmit,
  spotQuote,
  handleRejectRate,
}: BidRateFieldProps) {
  const {
    quote: { stops },
    status,
  } = spotQuote
  const [isSubmitDialogOpened, setIsSubmitDialogOpened] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const isExpired = isQuoteExpired(spotQuote.quote)
  const isRejected = isRateRejected(status)

  function handleSubmitButtonClicked() {
    setIsSubmitDialogOpened(true)
  }

  function handleConfirmSubmission() {
    onSubmit()
    setIsSubmitDialogOpened(false)
  }

  function handleCloseSubmissionDialog() {
    setIsSubmitDialogOpened(false)
  }

  function handleRejectRateButtonClicked() {
    setIsRejectDialogOpen(true)
  }

  function handleCloseRejectDialog() {
    setIsRejectDialogOpen(false)
  }

  function handleRejectConfirmed(reason: string) {
    handleRejectRate!(reason)
    setIsRejectDialogOpen(false)
  }

  return (
    <Layout.Stack>
      {isSubmitDialogOpened && (
        <ConfirmSubmissionDialog
          onConfirm={handleConfirmSubmission}
          onCancel={handleCloseSubmissionDialog}
          flatRate={currentFlatRate!}
          stops={stops}
          accessorialCharges={accessorialCharges}
          quoteAccessorials={spotQuote.quote.accessorials}
        />
      )}
      <ConfirmRejectDialog
        open={isRejectDialogOpen}
        onConfirm={handleRejectConfirmed}
        onCancel={handleCloseRejectDialog}
      />

      {!isExpired && (
        <Layout.Group gap="spacing-3" align="flex-end">
          <QuoteRateInputFields mileage={mileage} disabled={disabled} onRateChange={onRateChange} />
          <Layout.Group gap="spacing-4" align="flex-end">
            <Button
              onClick={handleRejectRateButtonClicked}
              variant="tertiary"
              disabled={spotQuote.status !== 'pending' && spotQuote.status !== 'available'}
            >
              Reject
            </Button>{' '}
            <Button variant="primary" disabled={submitDisabled} onClick={handleSubmitButtonClicked}>
              Submit
            </Button>
          </Layout.Group>
        </Layout.Group>
      )}
      {(isSubmitted || isRejected) && (
        <SubmittedRateValue
          initialFlatRate={initialFlatRate}
          initialPerMileRate={initialPerMileRate}
          currentFlatRate={currentFlatRate}
          mileage={mileage}
          submittedAt={submittedAt}
          rateStatus={status}
        />
      )}
    </Layout.Stack>
  )
}
